// src/views/AccueilAdmin.jsx — L'ACCUEIL DE L'ADMIN (lot 1).
//
// ⚠️ ÉCRAN NEUF. Il ne remplace rien, ne redessine rien : les huit onglets
// existants restent tels quels, l'accueil se pose DEVANT et RENVOIE vers eux.
//
// ⚠️ IL N'AJOUTE RIEN AUX 18 REQUÊTES DE LA CONNEXION (exigence Dimitri
// 2026-08-25). Ses lectures partent APRÈS le premier affichage — l'écran se
// dessine, PUIS il lit — il MONTRE SON ATTENTE, et il ne pré-charge AUCUNE
// feuille de détail : les trois lectures d'une feuille partent au clic.
//
// ⚠️⚠️ UNE SEULE SOURCE PAR CHIFFRE. Le « à décider » du bandeau EST
// `attend.length`, le même tableau que la carte rend ligne à ligne — il n'y a
// pas deux comptes, il n'y en a qu'un. Les arrivées et les départs passent par
// `getTodayMovements`, LE calcul de Calendrier > Aujourd'hui : les deux écrans
// ne peuvent pas diverger. C'est le défaut le plus répandu de l'admin actuel,
// et cet écran est celui où il se verrait le plus.
//
// ⚠️⚠️ CHAQUE CARTE DIT POURQUOI ELLE N'A RIEN. « On lit », « la lecture a
// échoué » et « il n'y a rien » sont trois phrases différentes — jamais un
// vide muet, jamais un « tout va bien » posé sur une requête ratée.

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { DS } from "../lib/ui";
import { supabase } from "../supabase";
import { getAllLogsFlat } from "../lib/sousloc-helpers";
import { resolveLogTime } from "../lib/menage-status-helpers";
import { needsEtatFinancierQualif, getDegs } from "../lib/incidents-helpers";
import { aircoverASaisirDeg } from "../lib/accueil-incident";
import { todayParisIso } from "./MenageTodayView";
import {
  construireAttend,
  partagerAttend,
  heureCourte,
} from "../lib/accueil-attend";
import {
  lignesMenages,
  lignesArrivees,
  lignesDeparts,
} from "../lib/accueil-menages";
import {
  filsEnAttente,
  resumeTaches,
  ligneProspection,
} from "../lib/accueil-cartes";
// ⚠️ CE QUE L'ÉCRAN AFFICHE, DIT À L'OPÉRATEUR (règle générale Dimitri
// 2026-09-12). Voir le cartouche de src/lib/accueil-releve.js.
import { composerReleveAccueil } from "../lib/accueil-releve";
import { garderCeQuiNaPasDeMaison } from "../lib/evenements-maison";
import { prospectsApi } from "../lib/prospects-api";
import {
  chargerMenages,
  chargerMouvements,
  chargerDemandesHoraire,
  chargerIncidents,
  chargerMessages,
  chargerTaches,
  chargerProspection,
  chargerAnnuaireAgents,
  chargerSignalementsVoyageur,
  chargerPropositionsOperateur,
  chargerVeillesOperateur,
  chargerEvenements,
  curseurEvenements,
  poserCurseurEvenements,
} from "../lib/accueil-api";
import { PhraseVide } from "./accueil/CarteAccueil";
import {
  BandeauProto,
  LigneMenageBandeau,
  LigneMouvementBandeau,
  ColonneProto,
  LigneCol,
  PageColonnes,
} from "./accueil/proto/EcranAccueilProto";
import ResaV2Modal from "../modals/ResaV2Modal";
// LES FEUILLES DU PROTOTYPE (fichiers livrés 2026-08-26) : le dessin vit dans
// proto/FeuillesAccueil.jsx, la logique dans ControleursFeuilles.jsx — et les
// classes viennent de la copie de docs/handoff/fbz-admin.css.
import "../styles/fbz-admin.css";
import {
  CtrlSansAgent,
  CtrlHoraire,
  CtrlJugement,
  CtrlSuivi,
  CtrlReprise,
  CtrlSansPersonne,
  CtrlControleEmbarque,
  CtrlFil,
  CtrlTaches,
  CtrlProspects,
  CtrlMenageDetail,
  CtrlDepartReporte,
  MicroAccueil,
  CtrlSignalementVoyageur,
} from "./accueil/ControleursFeuilles";
import { GRIS } from "./accueil/_tons";
// LE BLOC DE L'OPÉRATEUR — sa voix, en tête de l'accueil (décision 2026-09-07).
import BlocOperateur from "./accueil/BlocOperateur";
// « C'est traité » — et POURQUOI. Le motif est demandé, jamais supposé.
import FeuilleTraite from "./accueil/FeuilleTraite";
import { poserSilenceVeille } from "../lib/veille-api";

const VIDE = { ok: null, data: null, motif: null };
// Combien de fils la carte des messages LISTE (le compteur, lui, dit le total).
// ⚠️ UNE SEULE DÉFINITION : l'opérateur reçoit exactement ce qui est listé, et
// une carte qui s'allongerait sans lui le rendrait à nouveau plus savant que
// l'écran de Dimitri.
const FILS_AFFICHES = 6;

/** L'état d'une source, traduit en la phrase que la carte doit dire. */
function etatDe(source) {
  if (!source || source.ok === null) return "lecture";
  return source.ok ? "vide" : "echec";
}

// ── POST authentifié — même pattern que les écrans existants ─────────────────
async function postAction(body) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Session expirée");
  const res = await fetch("/api/beds24-mapping", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `Erreur ${res.status}`);
  return json;
}

// Le comptage passe par le client partagé (src/lib/prospects-api) : un fetch
// maison recopiait l'URL, l'en-tête d'auth et la gestion d'erreur — trois
// occasions de diverger du reste de l'app à la première évolution.
const getProspectionCount = () => prospectsApi.fetchProspectsCount();

/** « Dimanche 23 août » — le jour de la semaine, autorisé ICI seulement. */
function dateEnToutesLettres() {
  const s = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Europe/Paris",
  }).format(new Date());
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function AccueilAdmin({
  cfg,
  blocksByMonth,
  onNavigate,
  onAller,
  currentUser,
}) {
  const todayIso = useMemo(() => todayParisIso(), []);
  const dateTexte = useMemo(() => dateEnToutesLettres(), []);

  const [menages, setMenages] = useState(VIDE);
  const [mouvements, setMouvements] = useState(VIDE);
  const [horaires, setHoraires] = useState(VIDE);
  const [incidents, setIncidents] = useState(VIDE);
  const [messages, setMessages] = useState(VIDE);
  const [taches, setTaches] = useState(VIDE);
  const [prospection, setProspection] = useState(VIDE);
  // Ce qu'un voyageur nous a dit — séjour + état des lieux d'arrivée, une
  // seule source (elle porte AUSSI son propre « actif », cf. plus bas).
  const [signalements, setSignalements] = useState(VIDE);
  // LOT 1 OPÉRATEUR — les réponses préparées, chargées pour LEURS SEULES
  // surfaces d'accueil (verdict FINAL 2026-09-06, arrêt 7) : le tag « prête »
  // sur la carte Messages et le bandeau-renvoi du fil. JAMAIS une ligne de
  // « Ce qui vous attend » — « compter deux fois la même chose, c'est du
  // bruit. Ce qui m'attend, c'est ce qui n'a pas d'autre maison. »
  const [propositions, setPropositions] = useState(VIDE);
  // LES VEILLES DE L'OPÉRATEUR — elles entrent dans « Ce qui vous attend »
  // parce qu'elles n'ont AUCUNE autre maison (cf. veillesOperateur dans
  // src/lib/accueil-attend.js). Les propositions, elles, en ont trois : elles
  // restent dehors, verdict du 2026-09-06 intact.
  const [veilles, setVeilles] = useState(VIDE);
  // Ce qu'il annonce au fil de l'eau — lu depuis le curseur du navigateur.
  const [annonces, setAnnonces] = useState(VIDE);

  const [ouvert, setOuvert] = useState(null); // le chiffre déplié
  // La feuille ouverte : { ligne, origine } — l'origine dit QUELLE ligne l'a
  // ouverte (le titre le répète, la décision correspondante passe en tête).
  const [feuille, setFeuille] = useState(null);
  const [mouvementOuvert, setMouvementOuvert] = useState(null); // arrivée/départ
  const [horaireOuvert, setHoraireOuvert] = useState(null); // { demande, cle }
  const [incidentOuvert, setIncidentOuvert] = useState(null); // { incident, cle, degForce }
  const [aircoverOuvert, setAircoverOuvert] = useState(null); // { incident, deg, cle }
  const [filOuvert, setFilOuvert] = useState(null); // conversation en feuille
  const [signalementOuvert, setSignalementOuvert] = useState(null); // { famille, id, cle, titre }
  const [controleEmbarque, setControleEmbarque] = useState(null); // « je contrôle moi-même »
  const [tachesOuvert, setTachesOuvert] = useState(false); // la feuille Mes tâches
  // ── LES CARTES NE SONT PLUS LA PAGE ────────────────────────────────────
  // Dimitri, 2026-09-13 : « L'accueil est devenu la page de l'opérateur. Le
  // bandeau reste. Mais sous lui, il n'y a plus que lui. Plus de cartes
  // empilées. » Elles n'ont pas disparu : « Montrer tout ce qu'il y a à faire »
  // les ramène ENTIÈRES, avec tous leurs gestes.
  const [cartesOuvertes, setCartesOuvertes] = useState(false);
  const [prospectsOuvert, setProspectsOuvert] = useState(false); // la feuille Prospection
  const [photoOuverte, setPhotoOuverte] = useState(null); // visionneuse (détail ménage)
  const [microOuvert, setMicroOuvert] = useState(false); // le panneau du micro
  // ⚠️ LE RETRAIT OPTIMISTE (règle « les trois bougent ensemble ou aucun ») :
  // une décision enregistrée retire SA ligne localement — le compteur de la
  // carte et le « à décider » du bandeau dérivent du MÊME tableau filtré,
  // donc les trois descendent d'un coup, sans rechargement. Le refetch
  // silencieux réconcilie derrière (et fait NAÎTRE la ligne AirCover quand le
  // jugement en ouvre une).
  const [sorties, setSorties] = useState(() => new Set());
  const [annuaire, setAnnuaire] = useState([]); // les comptes ménage/orch (correction 27/08)

  // ── Les résolveurs de noms et d'heures — depuis `cfg`, DÉJÀ en mémoire ────
  // ⚠️ `getAllLogsFlat` est LE parc canonique (dédup byId) : c'est lui qui
  // évite le « parc compté de cinq façons ».
  const parLogement = useMemo(() => {
    const m = {};
    for (const l of getAllLogsFlat(cfg)) m[l.id] = l;
    return m;
  }, [cfg]);
  const nomLogement = useCallback(
    (id) => (parLogement[id] && parLogement[id].nom) || id || "—",
    [parLogement],
  );
  // ⚠️ LA TABLE DES NOMS, pour que les dessins ne parlent pas en codes non
  // plus. On la dérive de la MÊME source que `nomLogement` : deux tables
  // construites séparément finiraient par se contredire, et l'écran dirait
  // deux noms différents pour le même logement.
  const nomsParId = useMemo(() => {
    const m = {};
    for (const [id, l] of Object.entries(parLogement))
      if (l && l.nom) m[id] = l.nom;
    return m;
  }, [parLogement]);
  // Sous-loc = chambre (parentLog) OU proprio p6 OU facturation v2 — LE motif
  // d'IncidentModal, recopié à l'identique : FBZ y est l'hôte.
  const estSousLoc = useCallback(
    (logementId) => {
      const l = parLogement[logementId];
      return !!(
        l &&
        (l.parentLog || l.propId === "p6" || l.facturationSource === "v2")
      );
    },
    [parLogement],
  );
  const heureContratDe = useCallback(
    (id, kind) => resolveLogTime(parLogement[id], null, kind),
    [parLogement],
  );
  const heureFin = useCallback(
    (run) => heureContratDe(run && run.logement_id, "checkout"),
    [heureContratDe],
  );

  // LOT 4 OPÉRATEUR B : plus d'interrupteur d'envoi à relire — les vagues
  // d'emails de prospection n'existent plus (file in-app Logements › À démarcher).

  // ── LES BLOCS « RÉSA DIRECTE », DÉJÀ CHARGÉS PAR App.jsx ────────────────
  // ⚠️ ZÉRO REQUÊTE DE PLUS pour eux : `blocksByMonth` arrive de
  // `usePricingBlocksHistory`, qui tourne déjà pour l'admin. On l'aplatit.
  const blocsDirects = useMemo(() => {
    const out = [];
    if (!blocksByMonth || typeof blocksByMonth.forEach !== "function")
      return out;
    blocksByMonth.forEach((parLog) => {
      if (parLog && typeof parLog.forEach === "function") {
        parLog.forEach((liste) => {
          for (const b of liste || []) out.push(b);
        });
      }
    });
    return out;
  }, [blocksByMonth]);

  // ── LES LECTURES — APRÈS LE PREMIER AFFICHAGE, jamais pendant ────────────
  // ⚠️ Le `setTimeout(0)` n'est pas une coquetterie : il garantit que le
  // premier rendu (bandeau + cartes en attente) est PEINT avant qu'une seule
  // requête ne parte. L'écran montre son attente au lieu de rester blanc.
  const vivantRef = useRef(true);
  // ⚠️ Le montage REPOSE le drapeau : en StrictMode, React monte → démonte →
  // remonte, et un `useRef(true)` seul reste à `false` après le faux
  // démontage — plus AUCUNE donnée n'arrivait (écran figé sur « On lit… »,
  // attrapé par le test du parcours).
  useEffect(() => {
    vivantRef.current = true;
    return () => {
      vivantRef.current = false;
    };
  }, []);
  const chargerTout = useCallback(() => {
    const pose = (setter) => (p) =>
      p.then((r) => {
        if (vivantRef.current) setter(r);
      });
    pose(setMenages)(chargerMenages(supabase, todayIso));
    pose(setMouvements)(chargerMouvements(supabase, todayIso, blocsDirects));
    pose(setHoraires)(chargerDemandesHoraire(postAction));
    pose(setIncidents)(chargerIncidents(supabase, todayIso));
    pose(setMessages)(chargerMessages(supabase));
    pose(setTaches)(chargerTaches(supabase));
    pose(setProspection)(chargerProspection(getProspectionCount));
    pose(setAnnuaire)(chargerAnnuaireAgents(supabase));
    pose(setSignalements)(chargerSignalementsVoyageur(postAction));
    pose(setPropositions)(chargerPropositionsOperateur(postAction));
    pose(setVeilles)(chargerVeillesOperateur(postAction));
    // ⚠️ LE CURSEUR N'AVANCE QU'APRÈS UNE LECTURE RÉUSSIE. L'avancer avant
    // ferait disparaître, en cas de panne, des annonces que personne n'a vues :
    // un fil vide et un fil perdu ont exactement la même tête.
    pose((r) => {
      setAnnonces(r);
      if (r.ok === true && r.data.jusqua) poserCurseurEvenements(r.data.jusqua);
    })(chargerEvenements(postAction, curseurEvenements()));
  }, [todayIso, blocsDirects]);
  useEffect(() => {
    const id = setTimeout(chargerTout, 0);
    return () => clearTimeout(id);
  }, [chargerTout]);
  // ⚠️ L'ÉCRAN QU'ON LAISSE OUVERT SE RAFRAÎCHIT SEUL (correction 27/08) :
  // au retour de focus, au retour d'onglet, et toutes les 3 minutes tant
  // qu'il est visible — le « 6 à décider » de 8h ne survit plus à midi.
  useEffect(() => {
    const surRetour = () => {
      if (!document.hidden) chargerTout();
    };
    window.addEventListener("focus", surRetour);
    document.addEventListener("visibilitychange", surRetour);
    const id = setInterval(surRetour, 3 * 60 * 1000);
    return () => {
      window.removeEventListener("focus", surRetour);
      document.removeEventListener("visibilitychange", surRetour);
      clearInterval(id);
    };
  }, [chargerTout]);

  // ⚠️ MÉMOÏSÉS : sans cela, le repli `|| []` fabrique un tableau NEUF à chaque
  // rendu et tous les calculs en dessous se refont pour rien.
  const runs = useMemo(
    () => (menages.data && menages.data.runs) || [],
    [menages.data],
  );
  // Le roster « qui peut prendre / contrôler » : les adresses DISTINCTES vues
  // sur la fenêtre chargée — la dérivation de MenagesOpsTab, volontairement
  // bornée à l'existant (pas de nouvel endpoint d'annuaire).
  // Chaque option porte LA CHARGE DU JOUR de l'agent (spec Dimitri
  // 2026-08-26) : les occupés restent choisissables — la feuille les rend en
  // AMBRE et dit ce qu'ils ont déjà, la décision reste à l'admin.
  const optionsAgents = useMemo(() => {
    const vus = new Set();
    const opts = [];
    // ⚠️ L'ANNUAIRE D'ABORD (correction 27/08) : un agent SANS ménage récent
    // (nouveau, ou de retour de congé) doit être choisissable — la fenêtre de
    // runs ne suffit pas le jour où on le cherche. Les runs complètent la
    // charge du jour ; l'annuaire garantit la présence.
    const emails = [
      ...new Set([
        ...(annuaire || []),
        ...runs.map((r) => r && r.assigned_provider_email).filter(Boolean),
      ]),
    ];
    for (const e of emails) {
      if (vus.has(e)) continue;
      vus.add(e);
      const prenom = e.split("@")[0];
      const duJour = runs.filter(
        (x) =>
          x.scheduled_date === todayIso &&
          x.assigned_provider_email === e &&
          x.status !== "skipped",
      );
      const restants = duJour.filter((x) => x.status !== "completed");
      opts.push({
        value: e,
        label: prenom.charAt(0).toUpperCase() + prenom.slice(1),
        occupe: restants.length > 0,
        charge:
          duJour.length === 0
            ? "rien ce jour"
            : `${duJour.length} ménage${duJour.length > 1 ? "s" : ""} · ${duJour
                .map((x) => nomLogement(x.logement_id))
                .join(", ")}`,
      });
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [runs, todayIso, nomLogement, annuaire]);
  const controles = useMemo(
    () => (menages.data && menages.data.controles) || {},
    [menages.data],
  );

  // ── LA LISTE — LE tableau, celui que le bandeau compte et que la carte rend
  const attend = useMemo(
    () =>
      construireAttend(
        {
          runs,
          demandesHoraire: (horaires.data && horaires.data.pending) || [],
          incidents: incidents.data || [],
          controles: Object.values(controles),
          // ⚠️ SEULEMENT si la source est VIVANTE : quand la migration n'est pas
          // appliquée, `actif` est faux et la liste est vide — on ne mélange pas
          // « rien à traiter » avec « pas encore branché ». Le second se DIT, en
          // bas de l'écran, plutôt que de se taire.
          signalements:
            (signalements.data && signalements.data.signalements) || [],
          // ⚠️ SEULEMENT si la lecture a réussi : une veille en panne ne doit pas
          // se lire « rien à signaler ». L'échec se DIT en bas de l'écran, comme
          // pour les signalements et l'opérateur dormant.
          veilles:
            (veilles.ok === true && veilles.data && veilles.data.alertes) || [],
          // Verdict FINAL 2026-09-06 (arrêt 7) : les propositions de l'opérateur
          // N'ENTRENT PLUS ici — « compter deux fois la même chose, c'est du
          // bruit ». Leurs surfaces d'accueil : le tag « prête » (carte Messages),
          // le bandeau-renvoi du fil, le point d'état de la pilule d'en-tête.
        },
        {
          nomLogement,
          heureFin,
          // ⚠️ Sans lui, deux ménages sans agent de deux jours différents sur le
          // même logement donnent deux lignes identiques (défaut vu en prod).
          todayIso,
          estATrancher: needsEtatFinancierQualif,
          degradationsDe: getDegs,
          // La dérivation RÉELLE (décidée & rien saisi), sous-loc comprise.
          estAircoverASaisir: (d, i) =>
            aircoverASaisirDeg(d, { sousLoc: estSousLoc(i.logement_id) }),
          // Un contrôle existe déjà pour ce run → il n'attend plus personne.
          estControle: (run) => !!controles[run.id],
        },
      ),
    [
      runs,
      horaires.data,
      incidents.data,
      controles,
      signalements.data,
      veilles,
      nomLogement,
      heureFin,
      todayIso,
      estSousLoc,
    ],
  );

  const sortir = useCallback(
    (cle) => {
      setSorties((prev) => {
        const x = new Set(prev);
        x.add(cle);
        return x;
      });
      chargerTout();
    },
    [chargerTout],
  );
  // Quand le serveur confirme la disparition, la clé sort du masque : si la
  // MÊME situation renaît plus tard (agent retiré à nouveau), la ligne revient.
  useEffect(() => {
    setSorties((prev) => {
      if (prev.size === 0) return prev;
      const presentes = new Set(attend.map((a) => a.cle));
      const x = new Set([...prev].filter((c) => presentes.has(c)));
      return x.size === prev.size ? prev : x;
    });
  }, [attend]);
  // La ligne dont on est en train de dire pourquoi elle est traitée.
  const [aTaire, setATaire] = useState(null);
  // Les alertes tues À L'ÉCRAN, en attendant le prochain chargement : sans ça,
  // la ligne resterait jusqu'au rafraîchissement et on cliquerait deux fois.
  const [tues, setTues] = useState(() => new Set());

  const attendVisible = useMemo(
    () => attend.filter((a) => !sorties.has(a.cle) && !tues.has(a.cle)),
    [attend, sorties, tues],
  );

  // ── DEUX LISTES, ET LES MESSAGES DEHORS ────────────────────────────────
  // Dimitri, 2026-09-12 : « ce qui vous attend doit être ce que je fais
  // AUJOURD'HUI. Ce que tu y mets, c'est de la surveillance. » Et : « un
  // message attend une réponse depuis 73 jours n'a rien à faire là — j'ai déjà
  // une section Messages en attente juste en dessous. »
  const { attend: attendDuJour, surveiller } = useMemo(
    () => partagerAttend(attendVisible),
    [attendVisible],
  );
  // La surveillance s'ouvre d'un clic et se referme : par défaut REPLIÉE, avec
  // son seul compte. Vingt-deux lignes d'affilée ne se lisent pas.
  const [surveillanceOuverte, setSurveillanceOuverte] = useState(false);
  const nSurveiller = useMemo(
    () => surveiller.reduce((n, g) => n + g.n, 0),
    [surveiller],
  );

  // ── LE RENDU D'UNE LIGNE, EXTRAIT ────────────────────────────────────────
  // Extrait tel quel (move byte-identique) le 2026-09-12 pour servir DEUX
  // sections : « Ce qui vous attend » et « À surveiller ». Le dupliquer aurait
  // garanti qu'un correctif n'atteigne qu'une des deux — et c'est toujours
  // celle qu'on ne regarde pas.
  // ── OUVRIR UNE LIGNE — extrait tel quel (move byte-identique) ─────────
  // Le 2026-09-13 : la même ligne se rend maintenant à DEUX endroits — dans la
  // carte, et dans la liste compacte du point d'état. Un second `onClic`
  // recopié se serait désynchronisé au premier ajout de nature, en silence :
  // la ligne s'ouvrirait ici et pas là.
  const ouvrirLigneAttend = (a) => {
    // UNE VEILLE MÈNE À SON ÉCRAN — la destination vient du
    // moteur, jamais devinée ici. Sans elle, la ligne constate
    // sans permettre d'agir : « je l'ouvre deux fois et j'arrête ».
    if (a.source === "operateur") {
      if (a.destination) allerVers(a.destination);
      else aller("operateur");
      return;
    }
    // ⚠️ LE PRINCIPE DE L'ÉCRAN : la ligne ouvre une FEUILLE
    // par-dessus, on décide, on retombe ici avec une ligne de
    // moins.
    if (a.nature === "signalement_voyageur") {
      // ⚠️ LE TEXTE DU VOYAGEUR PART AU CLIC, jamais avant :
      // la liste ne transporte pas ce qu'il a écrit.
      setSignalementOuvert({
        famille: a.cible.famille,
        id: a.cible.id,
        cle: a.cle,
        titre: a.titre,
        sousTitre: a.sousTitre,
      });
      return;
    }
    if (a.nature === "depart_reporte") {
      setFeuille({
        origine: "depart-reporte",
        cle: a.cle,
        ligne: {
          runId: a.cible.runId,
          titre: a.titre,
          sousTitre: a.sousTitre,
        },
      });
      return;
    }
    if (
      a.nature === "menage_sans_agent" ||
      a.nature === "reprise_en_attente" ||
      a.nature === "controle_sans_personne"
    ) {
      const runId = a.cible.runId;
      const lj = lignesJour.find((l) => l.runId === runId);
      const origine =
        a.nature === "menage_sans_agent"
          ? "sans-agent"
          : a.nature === "reprise_en_attente"
            ? "reprise"
            : "ctrl-orphelin";
      setFeuille({
        origine,
        cle: a.cle,
        ligne: lj || {
          runId,
          titre: (a.sousTitre || "").split(" · ")[0],
          sousTitre: a.sousTitre,
        },
      });
      return;
    }
    if (a.nature === "demande_horaire") {
      const d = ((horaires.data && horaires.data.pending) || []).find(
        (x) => x.id === (a.cible && a.cible.id),
      );
      if (d) {
        setHoraireOuvert({ demande: d, cle: a.cle });
        return;
      }
    }
    if (a.nature === "incident_a_trancher") {
      const inc = (incidents.data || []).find(
        (x) => x.id === (a.cible && a.cible.id),
      );
      if (inc) {
        setIncidentOuvert({
          incident: inc,
          cle: a.cle,
          degForce: null,
        });
        return;
      }
    }
    if (a.nature === "aircover_a_saisir") {
      const inc = (incidents.data || []).find(
        (x) => x.id === (a.cible && a.cible.id),
      );
      if (inc) {
        const deg = (getDegs(inc) || []).find((d) =>
          aircoverASaisirDeg(d, {
            sousLoc: estSousLoc(inc.logement_id),
          }),
        );
        if (deg) {
          setAircoverOuvert({
            incident: inc,
            deg,
            cle: a.cle,
          });
          return;
        }
      }
    }
    aller("incidents");
  };

  const rendreLigneAttend = (a) => {
    // La pastille vient de la nature ; la COULEUR vient de la ligne dès
    // que la nature n'en impose pas une (cas des signalements, dont le
    // ton dépend de la famille).
    const base = pcAttend[a.nature] || { pc: "!" };
    const k = {
      pc: base.pc,
      terra: base.terra === undefined ? a.ton === "terra" : base.terra,
    };
    return (
      <LigneCol
        key={a.cle}
        testId={`attend-${a.cle}`}
        terra={k.terra}
        pc={k.pc}
        pcTerra={k.terra}
        pcStyle={
          k.terra
            ? undefined
            : {
                background: "var(--ambre-bg)",
                color: "var(--ambre-tx)",
              }
        }
        titre={a.titre}
        sous={a.sousTitre}
        // ── « C'EST TRAITÉ » ───────────────────────────
        // Seules les lignes de VEILLE peuvent être tues : les
        // autres (un ménage sans agent, un incident à juger)
        // décrivent un travail à faire, pas un signal — les
        // taire les ferait disparaître sans que rien ne soit
        // fait.
        action={
          a.taire && a.taire.famille ? (
            <button
              type="button"
              data-testid={`traite-${a.cle}`}
              title="C'est traité"
              aria-label={`Marquer traité : ${a.titre}`}
              onClick={() => setATaire(a)}
              style={{
                width: 38,
                borderRadius: 10,
                border: "1px solid var(--bord, #e3e8ee)",
                background: "#fff",
                color: "#5c6773",
                cursor: "pointer",
                flexShrink: 0,
                fontSize: 15,
              }}
            >
              ✓
            </button>
          ) : undefined
        }
        source={a.source === "operateur" ? "l'opérateur" : undefined}
        onClic={() => ouvrirLigneAttend(a)}
      />
    );
  };

  const lignesJour = useMemo(
    () =>
      lignesMenages(runs, {
        todayIso,
        nomLogement,
        // « avant 17h » — le format de l'application, jamais « 17:00 ».
        heureDe: (r) => {
          const h = heureCourte(heureFin(r));
          return h ? `avant ${h}` : null;
        },
        controles,
        incidents: incidents.data || [],
      }),
    [runs, todayIso, nomLogement, heureFin, controles, incidents.data],
  );

  const arrivees = useMemo(
    () =>
      lignesArrivees((mouvements.data && mouvements.data.checkins) || [], {
        nomLogement,
        heureContratDe,
      }),
    [mouvements.data, nomLogement, heureContratDe],
  );
  const departs = useMemo(
    () =>
      lignesDeparts((mouvements.data && mouvements.data.checkouts) || [], {
        nomLogement,
        heureContratDe,
      }),
    [mouvements.data, nomLogement, heureContratDe],
  );

  // D1 — les fils dont une réponse est PRÊTE portent le tag « prête » (le fil
  // s'ouvrira en MODE RENVOI — la décision vit dans « L'opérateur »).
  const filsAvecProposition = useMemo(
    () =>
      new Set(
        ((propositions.data && propositions.data.propositions) || [])
          .map((p) => p && p.thread_id)
          .filter(Boolean),
      ),
    [propositions.data],
  );
  const fils = useMemo(
    () =>
      filsEnAttente(messages.data || [], {
        todayIso,
        nomLogement,
        filsAvecProposition,
      }),
    [messages.data, todayIso, nomLogement, filsAvecProposition],
  );
  // ⚠️ Un fil tu disparaît TOUT DE SUITE — sinon on recliquerait en attendant
  // le rafraîchissement, et le second motif écraserait le premier.
  const filsVisibles = useMemo(
    () => fils.filter((m) => !tues.has(m.cle)),
    [fils, tues],
  );
  // ── CE QUE L'OPÉRATEUR VERRA — EXACTEMENT CE QUI EST PEINT ──────────────
  //
  // Dimitri, 2026-09-12 : « L'opérateur et l'accueil sont sur le même écran. Il
  // devrait voir exactement ce que je vois, pas sa propre version. […] Si je
  // vois "Chloé Raynal, Cocon Naturel, le 10 septembre", il doit voir la même
  // ligne, pas la sienne. »
  //
  // ⚠️ ON PART DES LISTES RENDUES, PAS DE LEURS SOURCES. `attendDuJour`,
  // `surveiller` et `filsVisibles` sont ce que le navigateur affiche à la
  // seconde près : déjà triées, déjà partagées, déjà privées de ce que Dimitri
  // vient de taire d'un clic. Repartir des données brutes ici recréerait
  // exactement la seconde vérité qu'on ferme.
  //
  // ⚠️ ET L'ÉTAT DES SOURCES VOYAGE AVEC. Trois listes vides peuvent dire « ton
  // écran est calme » ou « ton écran n'a rien pu lire » — le serveur doit
  // pouvoir faire la différence, sinon il annonce le calme sur une panne.
  const releveAccueil = useMemo(
    () =>
      composerReleveAccueil({
        jour: todayIso,
        // Le haut de l'écran : les trois comptes qu'il lit en premier.
        bandeau: {
          menages: lignesJour.map((l) => ({
            titre: l.titre,
            sousTitre: l.sousTitre,
            // « terra » = personne sur ce ménage. C'est la couleur qu'il voit.
            seul: l.ton === "terra",
          })),
          arrivees: arrivees.map((a) => ({ titre: a.titre })),
          departs: departs.map((d) => ({ titre: d.titre })),
          aDecider: attendVisible.length,
        },
        attend: attendDuJour,
        surveiller,
        // ⚠️ CE QUI EST LISTÉ, pas ce qui existe : la carte n'affiche que les
        // six premiers fils. Lui en envoyer quinze le ferait parler de lignes
        // que Dimitri n'a pas sous les yeux — la même divergence, dans l'autre
        // sens. Le compte vrai part à côté, comme sur la carte.
        messages: filsVisibles.slice(0, FILS_AFFICHES),
        totaux: { messages: filsVisibles.length },
        sources: {
          menages: menages.ok,
          horaires: horaires.ok,
          incidents: incidents.ok,
          signalements: signalements.ok,
          veilles: veilles.ok,
          messages: messages.ok,
        },
      }),
    [
      todayIso,
      lignesJour,
      arrivees,
      departs,
      attendVisible,
      attendDuJour,
      surveiller,
      filsVisibles,
      menages.ok,
      horaires.ok,
      incidents.ok,
      signalements.ok,
      veilles.ok,
      messages.ok,
    ],
  );

  // ── CE QUI RESTE À ANNONCER ────────────────────────────────────────────
  // « Ce qui a une maison y va » : l'écran retire du fil ce qu'il affiche déjà
  // dans sa liste. Le serveur annonce, l'écran range — lui seul sait ce qui est
  // sous les yeux de Dimitri à cette seconde.
  const evenements = useMemo(() => {
    const brut = (annonces.ok === true && annonces.data.evenements) || [];
    return garderCeQuiNaPasDeMaison(brut, {
      fils: new Set(filsVisibles.map((m) => m.cible && m.cible.threadId)),
      runs: new Set(
        attendDuJour.map((a) => a.cible && a.cible.runId).filter(Boolean),
      ),
    });
  }, [annonces, filsVisibles, attendDuJour]);

  const resume = useMemo(
    () => resumeTaches(taches.data || [], { todayIso, nomLogement }),
    [taches.data, todayIso, nomLogement],
  );

  // ── SEPT LIGNES, ET LEUR VRAI GESTE ────────────────────────────────────
  //
  // Dimitri, 2026-09-13 : « Sept lignes, chacune avec son point de couleur et
  // son vrai geste — pas un résumé. Les coches "Rien à répondre" y sont. »
  //
  // ⚠️ ELLES VIENNENT DES LISTES PEINTES, comme le relevé de l'opérateur : le
  // même `ouvrirLigneAttend`, la même feuille « c'est traité », le même ordre.
  // Une seconde composition aurait divergé de la carte au premier ajout — et
  // l'écart se serait vu seulement le jour où l'une ouvre et l'autre pas.
  //
  // ⚠️ ET LES RÉSUMÉS RESTENT DES RÉSUMÉS. « 4 tâches », « 22 à surveiller » :
  // ces deux-là ne portent pas un geste unique — leur ligne EMMÈNE vers la
  // liste, elle ne prétend pas la remplacer.
  const aFaire = useMemo(() => {
    const L = [];
    for (const a of attendDuJour) {
      L.push({
        cle: a.cle,
        ton: a.ton === "terra" ? "u" : "a",
        titre: a.titre,
        sous: a.sousTitre,
        onClic: () => ouvrirLigneAttend(a),
        onTraite: a.taire && a.taire.famille ? () => setATaire(a) : null,
      });
    }
    for (const m of filsVisibles) {
      L.push({
        cle: m.cle,
        ton: "a",
        titre: m.titre,
        sous: m.sousTitre,
        tag: m.tag,
        onClic: () =>
          setFilOuvert({ threadId: m.cible.threadId, titre: m.titre }),
        onTraite: () =>
          setATaire({
            ...m,
            taire: { famille: "fil_sans_reponse", cible: m.cible.threadId },
          }),
      });
    }
    if (resume.total > 0)
      L.push({
        cle: "taches",
        ton: "a",
        titre: `${resume.total} tâche${resume.total > 1 ? "s" : ""} à faire`,
        sous: resume.lignes[0] ? resume.lignes[0].titre : "",
        onClic: () => setTachesOuvert(true),
      });
    if (nSurveiller > 0)
      L.push({
        cle: "surveiller",
        ton: "c",
        titre: `${nSurveiller} chose${nSurveiller > 1 ? "s" : ""} à surveiller`,
        sous: "rien qui presse",
        onClic: () => {
          setCartesOuvertes(true);
          setSurveillanceOuverte(true);
        },
      });
    // ⚠️ PLUS DE COUPE À SEPT (2026-09-15). Ces lignes alimentent désormais la
    // liste UNIQUE « Ce qui reste à faire », et la pastille en compte les
    // lignes : couper ici ferait mentir le compteur, qui est précisément le
    // défaut qu'on vient de fermer. Dimitri : « pas de maximum ».
    return { lignes: L, total: L.length };
  }, [attendDuJour, filsVisibles, resume, nSurveiller]);

  // ⚠️ Un chiffre qu'on ne SAIT PAS encore ne s'écrit pas « 0 » : « … » dit
  // qu'on lit. Écrire zéro pendant le chargement, c'est affirmer qu'il n'y a
  // rien alors qu'on n'en sait rien.
  const nombre = (source, valeur) => (source.ok === true ? valeur : "…");

  const aller = useCallback(
    (ecran) => {
      onNavigate && onNavigate(ecran);
    },
    [onNavigate],
  );
  // Navigation PROFONDE (écran + référence) — la même que partout ailleurs
  // (App.jsx:313). Sans référence, ouvrir « Messages » renvoie à la recherche.
  const allerVers = useCallback(
    (d) => {
      if (onAller && d && d.onglet) onAller(d);
      else if (d && d.onglet) aller(d.onglet);
    },
    [onAller, aller],
  );

  // Les deux cases du proto : départ du voyageur / arrivée du suivant —
  // PLEIN = heure du formulaire, POINTILLÉS = heure du contrat.
  const creneauDe = useCallback(
    (runId) => {
      const run = runs.find((r) => r.id === runId);
      if (!run) return null;
      const lid = run.logement_id;
      const co = ((mouvements.data && mouvements.data.checkouts) || []).find(
        (m) => m.logement_id === lid,
      );
      const ci = ((mouvements.data && mouvements.data.checkins) || []).find(
        (m) => m.logement_id === lid,
      );
      const dReel = co && (co.guest_departure_time || null);
      const aReel = ci && (ci.guest_arrival_time || ci.arrival_time || null);
      const l = parLogement[lid];
      const dmin = l && Number(l.menage_duree_min ?? l.dureeMenage);
      const fmt = (t) => (t ? heureCourte(t) : null);
      return {
        departH: fmt(dReel) || fmt(heureContratDe(lid, "checkout")),
        departSur: !!dReel,
        arriveeH: fmt(aReel) || fmt(heureContratDe(lid, "checkin")),
        arriveeSur: !!aReel,
        duree:
          Number.isFinite(dmin) && dmin > 0
            ? `${Math.floor(dmin / 60) ? `${Math.floor(dmin / 60)} h ` : ""}${dmin % 60 || ""}`.trim()
            : null,
      };
    },
    [runs, mouvements.data, parLogement, heureContratDe],
  );
  const proprioNomDe = useCallback(
    (logementId) => {
      for (const pp of (cfg && cfg.props) || []) {
        if (
          (pp.logs || []).some(
            (l) =>
              l.id === logementId ||
              (l.chambres || []).some((c) => c.id === logementId),
          )
        )
          return pp.nom;
      }
      return null;
    },
    [cfg],
  );

  // ── les lignes du bandeau déplié — le balisage du fichier, nos données ──
  const pcAttend = {
    // Ce qu'un voyageur nous dit — l'enveloppe : c'est un message, pas une
    // alerte de production. ⚠️ PAS de `terra` figé ici : le ton vient de LA
    // LIGNE (terracotta quand il signale pendant le séjour, ambre pour un
    // constat d'arrivée). Un ton figé aurait peint les deux en rouge.
    signalement_voyageur: { pc: "✉" },
    menage_sans_agent: { pc: "⚠", terra: true },
    demande_horaire: { pc: "◷", terra: false },
    incident_a_trancher: { pc: "⚠", terra: true },
    aircover_a_saisir: { pc: "◪", terra: false },
    reprise_en_attente: { pc: "◉", terra: true },
    controle_sans_personne: { pc: "◉", terra: false },
  };
  const tipOeil = {
    quelquun_dessus: "ctrl_designe",
    personne_dessus: "ctrl_personne",
    rien_a_signaler: "ctrl_ras",
    a_reprendre: "ctrl_reprendre",
  };
  const canalDe = (m) =>
    m && m.kind === "pricing_block"
      ? "Direct"
      : m && m.source === "airbnb"
        ? "Airbnb"
        : m && m.source === "booking"
          ? "Booking"
          : m && m.source
            ? m.source.charAt(0).toUpperCase() + m.source.slice(1)
            : "Direct";
  const ouvrirDetailMenage = (l) => setFeuille({ origine: "menage", ligne: l });

  return (
    <div data-testid="accueil-admin">
      <BandeauProto
        dateTexte={dateTexte}
        salutation="Bonjour Dimitri & Néïsia"
        aDecider={nombre(menages, attendVisible.length)}
        // Le toucher du gros chiffre FAIT DÉFILER jusqu'à sa carte — pas de
        // navigation, pas de dépli : la carte est déjà sur cette page.
        onADecider={() => {
          const carte = document.querySelector('[data-testid="carte-attend"]');
          if (carte)
            carte.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        chiffres={[
          // ⚠️ LE SINGULIER SE DIT AUSSI (défaut vu à l'écran).
          {
            cle: "menages",
            n: nombre(menages, lignesJour.length),
            libelle: `${lignesJour.length > 1 ? "ménages" : "ménage"} aujourd'hui`,
          },
          {
            cle: "arrivees",
            n: nombre(mouvements, arrivees.length),
            libelle: arrivees.length > 1 ? "arrivées" : "arrivée",
          },
          {
            cle: "departs",
            n: nombre(mouvements, departs.length),
            libelle: departs.length > 1 ? "départs" : "départ",
          },
        ]}
        detail={ouvert}
        onDetail={(cle) => setOuvert((v) => (v === cle ? null : cle))}
        detailEnfants={
          ouvert === "menages"
            ? lignesJour.map((l) => (
                <LigneMenageBandeau
                  key={l.cle}
                  testId={`detail-${l.cle}`}
                  ini={l.pastille}
                  sansAgent={l.ton === "terra"}
                  logement={l.titre}
                  sous={l.sousTitre}
                  tips={[
                    // ⚠️ L'ORDRE DU FICHIER : incident d'abord, la validation,
                    // puis l'œil du contrôle. Un tip cliquable ouvre le DÉTAIL du
                    // ménage (le geste du proto : feuilleMenage + la vue visée).
                    ...(l.incident
                      ? [
                          {
                            variante:
                              l.incident === "a_juger"
                                ? "incident_a_juger"
                                : "incident_regle",
                            onClic: () => ouvrirDetailMenage(l),
                            testId: `${l.cle}-tip-incident`,
                          },
                        ]
                      : []),
                    { variante: l.valide ? "valide" : "pas_valide" },
                    ...(tipOeil[l.oeil]
                      ? [
                          {
                            variante: tipOeil[l.oeil],
                            onClic: () => ouvrirDetailMenage(l),
                            testId: `${l.cle}-tip-oeil`,
                          },
                        ]
                      : []),
                  ]}
                  onOuvrir={() => ouvrirDetailMenage(l)}
                />
              ))
            : ouvert === "arrivees"
              ? arrivees.map((l) => {
                  const m = (
                    (mouvements.data && mouvements.data.checkins) ||
                    []
                  ).find((x) => x.id === l.cible.resaId);
                  return (
                    <LigneMouvementBandeau
                      key={l.cle}
                      testId={`detail-${l.cle}`}
                      depart={false}
                      logement={l.titre}
                      sous={`${l.sousTitre} · ${canalDe(m)}`}
                      heure={l.heure && l.heure.heure}
                      heureSure={!!(l.heure && l.heure.sur)}
                      heureSource={l.heure && l.heure.source}
                      onOuvrir={() =>
                        setMouvementOuvert({
                          ...l,
                          kind: (m && m.kind) || "reservation",
                        })
                      }
                    />
                  );
                })
              : ouvert === "departs"
                ? departs.map((l) => {
                    const m = (
                      (mouvements.data && mouvements.data.checkouts) ||
                      []
                    ).find((x) => x.id === l.cible.resaId);
                    return (
                      <LigneMouvementBandeau
                        key={l.cle}
                        testId={`detail-${l.cle}`}
                        depart
                        logement={l.titre}
                        sous={`${l.sousTitre} · ${canalDe(m)}`}
                        heure={l.heure && l.heure.heure}
                        heureSure={!!(l.heure && l.heure.sur)}
                        heureSource={l.heure && l.heure.source}
                        onOuvrir={() =>
                          setMouvementOuvert({
                            ...l,
                            kind: (m && m.kind) || "reservation",
                          })
                        }
                      />
                    );
                  })
                : null
        }
      />

      {/* ── L'OPÉRATEUR, EN TÊTE — sa voix, dans SON style (fond ciel), pas
          celui du bandeau navy juste au-dessus. */}
      {/* ⚠️ LA FEUILLE NE SE FERME QU'À LA CONFIRMATION. Fermer avant
          donnerait l'impression que c'est fait, et la ligne reviendrait au
          prochain chargement sans qu'on comprenne pourquoi. */}
      <FeuilleTraite
        ligne={aTaire}
        onFermer={() => setATaire(null)}
        onTaire={async (arg) => {
          await poserSilenceVeille(arg);
          // Elle disparaît de l'écran TOUT DE SUITE — sinon on cliquerait deux
          // fois en attendant le rafraîchissement.
          setTues((v) => new Set(v).add(aTaire.cle));
        }}
      />

      {/* ⚠️ PLUS D'ARRIVÉES, DE MÉNAGES NI DE VEILLES ICI (2026-09-15). Elles
          n'alimentaient QUE la phrase de journée du bandeau, retiré à la
          demande de Dimitri (« au-dessus de la conversation, ça ne sert à
          rien »). Elles restent lues par l'accueil pour ses propres listes. */}
      <BlocOperateur
        nbPropositions={
          propositions.ok === true &&
          propositions.data &&
          propositions.data.actif !== false
            ? (propositions.data.propositions || []).length
            : 0
        }
        motifDormant={
          propositions.ok === true &&
          propositions.data &&
          propositions.data.actif === false
            ? propositions.data.motif
            : null
        }
        onOuvrir={() => aller("operateur")}
        // ⚠️ IL PARLE EN NOMS, JAMAIS EN CODES (Dimitri, 2026-09-09). Le
        // serveur traduit sa PHRASE ; la grille, elle, reçoit la table et
        // traduit son affichage. Le code reste dans la donnée : c'est lui qui
        // ouvre la bonne fiche au clic.
        noms={nomsParId}
        // Ce que Dimitri regarde en ce moment. Le contexte nourrit la
        // CONVERSATION, jamais un geste — un mouvement d'argent ne doit pas
        // dépendre de l'écran ouvert (lib/operateur-contexte.js).
        ecran="accueil"
        // ⚠️ ET CE QU'IL AFFICHE, LIGNE POUR LIGNE. `ecran` dit OÙ il est ;
        // ceci dit ce qu'il A SOUS LES YEUX — c'est la seconde moitié, et
        // c'est elle qui fait foi côté serveur sur les trois listes.
        accueil={releveAccueil}
        // Sept lignes et leur vrai geste, derrière son point d'état.
        aFaire={aFaire}
        evenements={evenements}
        coupes={(annonces.ok === true && annonces.data.coupes) || 0}
        annoncesMotif={
          annonces.ok === false
            ? annonces.motif || "Les annonces n'ont pas pu être lues."
            : null
        }
        cartesOuvertes={cartesOuvertes}
        onVoirTout={() => setCartesOuvertes((v) => !v)}
        // « Il exécute une demande, il propose une suggestion. » Le serveur a
        // tranché lequel des deux : ici on ne fait qu'exécuter sa décision.
        onAller={(d) => {
          if (!d || !d.ecran) return;
          if (d.cible) allerVers({ onglet: d.ecran, ref: d.cible });
          else aller(d.ecran);
        }}
      />

      {/* ── LES CARTES — SUR DEMANDE, PLUS PAR DÉFAUT ────────────────────
          « Ce qui vous attend, Messages en attente, Mes tâches, À surveiller,
          Prospection : elles n'ont pas disparu. Elles vivent dans sa
          conversation, il les montre quand je les demande. » (Dimitri,
          2026-09-13)

          ⚠️ ELLES RESTENT MONTÉES DANS CE FICHIER, entières et avec tous leurs
          gestes — on ne les a pas réécrites en petit ailleurs. « Montrer tout
          ce qu'il y a à faire » les rend telles quelles : c'est la même carte,
          pas une seconde version qui divergerait. */}
      {cartesOuvertes && (
        <PageColonnes
          enfants={
            <>
              {/* ── CE QUI VOUS ATTEND — le cœur ──────────────────────────────── */}
              <ColonneProto
                ton="attend"
                titre="Ce qui vous attend"
                testId="carte-attend"
                compteur={menages.ok === true ? attendDuJour.length : null}
                enfants={
                  attendDuJour.length === 0 ? (
                    <PhraseVide
                      testId="carte-attend-vide"
                      etat={etatDe(menages)}
                      motif={menages.motif}
                      quandRien="Rien n'attend votre décision."
                    />
                  ) : (
                    attendDuJour.map((a) => rendreLigneAttend(a))
                  )
                }
              />

              {/* ── À SURVEILLER — CE QUI NE M'ATTEND PAS ─────────────────────
                Dimitri, 2026-09-12 : « ça ne m'attend pas, ça se corrige quand
                j'ai le temps. Crée une seconde section À surveiller, sous
                celle-là, REPLIÉE avec juste un compte. »
                ⚠️ Repliée par défaut, et regroupée : dix lignes « sans barème »
                ne disent pas dix fois plus qu'une seule — elles noient les huit
                autres natures. Le groupe se déplie, et chaque ligne y garde sa
                coche « c'est traité ». */}
              {nSurveiller > 0 && (
                <ColonneProto
                  ton="attend"
                  titre="À surveiller"
                  testId="carte-surveiller"
                  compteur={nSurveiller}
                  enfants={
                    <>
                      <button
                        type="button"
                        data-testid="surveiller-basculer"
                        aria-expanded={surveillanceOuverte}
                        onClick={() => setSurveillanceOuverte((v) => !v)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "9px 12px",
                          border: "none",
                          background: "transparent",
                          color: "#5c6773",
                          fontSize: 12.5,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {surveillanceOuverte ? "▾" : "▸"}{" "}
                        {surveillanceOuverte
                          ? "Replier"
                          : `${nSurveiller} chose${nSurveiller > 1 ? "s" : ""} à corriger quand vous aurez le temps`}
                      </button>
                      {surveillanceOuverte &&
                        surveiller.map((g) =>
                          // Un groupe d'UNE ligne se rend comme la ligne :
                          // l'encadrer d'un titre de groupe répéterait son texte.
                          g.n === 1 ? (
                            rendreLigneAttend(g.lignes[0])
                          ) : (
                            <details
                              key={g.nature}
                              data-testid={`surveiller-groupe-${g.nature}`}
                              style={{ padding: "0 2px 6px" }}
                            >
                              <summary
                                style={{
                                  cursor: "pointer",
                                  padding: "7px 10px",
                                  fontSize: 12.5,
                                  color: "#3b4652",
                                }}
                              >
                                {g.titre}
                              </summary>
                              {g.lignes.map((a) => rendreLigneAttend(a))}
                            </details>
                          ),
                        )}
                    </>
                  }
                />
              )}

              {/* ── MESSAGES EN ATTENTE — le fil s'ouvre EN FEUILLE ───────────── */}
              <ColonneProto
                ton="messages"
                titre="Messages en attente"
                testId="carte-messages"
                compteur={messages.ok === true ? filsVisibles.length : null}
                enfants={
                  filsVisibles.length === 0 ? (
                    <PhraseVide
                      testId="carte-messages-vide"
                      etat={etatDe(messages)}
                      motif={messages.motif}
                      quandRien="Aucun message n'attend de réponse."
                    />
                  ) : (
                    <>
                      {filsVisibles.slice(0, FILS_AFFICHES).map((m) => (
                        <LigneCol
                          key={m.cle}
                          testId={`message-${m.cle}`}
                          pc={m.initiales}
                          titre={m.titre}
                          sous={m.sousTitre}
                          tag={m.tag}
                          // ── « RIEN À RÉPONDRE ? » ──────────────────────────
                          // Dimitri, 2026-09-12 : « dans Messages en attente, il
                          // me faut la coche que j'avais mise dans la maquette —
                          // celle qui ouvre "Rien à répondre ?" avec les trois
                          // motifs. Elle n'a pas été montée. »
                          //
                          // ⚠️ C'est la MÊME feuille et le MÊME silence que les
                          // veilles (`veille_silence`, famille `fil_sans_reponse`)
                          // — une seconde mécanique pour « marquer traité »
                          // aurait divergé au premier correctif.
                          action={
                            <button
                              type="button"
                              data-testid={`message-traite-${m.cle}`}
                              title="Rien à répondre ?"
                              aria-label={`Marquer traité : ${m.titre}`}
                              onClick={(e) => {
                                // Sans ça, le clic ouvrirait AUSSI le fil : la
                                // feuille s'afficherait sous la conversation.
                                e.stopPropagation();
                                setATaire({
                                  ...m,
                                  taire: {
                                    famille: "fil_sans_reponse",
                                    cible: m.cible.threadId,
                                  },
                                });
                              }}
                              style={{
                                width: 38,
                                borderRadius: 10,
                                border: "1px solid var(--bord, #e3e8ee)",
                                background: "#fff",
                                color: "#5c6773",
                                cursor: "pointer",
                                flexShrink: 0,
                                fontSize: 15,
                              }}
                            >
                              ✓
                            </button>
                          }
                          onClic={() =>
                            setFilOuvert({
                              threadId: m.cible.threadId,
                              titre: m.titre,
                            })
                          }
                        />
                      ))}
                      {filsVisibles.length > 6 && (
                        // ⚠️ UN PLAFOND QUI NE SE DIT PAS EST INTERDIT (correction
                        // 27/08) : la carte montre 6 fils, le reste est NOMMÉ.
                        <LigneCol
                          testId="messages-et-autres"
                          pc="…"
                          titre={`et ${filsVisibles.length - 6} autre${filsVisibles.length - 6 > 1 ? "s" : ""}`}
                          sous="tout voir dans Messages"
                          onClic={() => aller("messages")}
                        />
                      )}
                    </>
                  )
                }
              />

              {/* ── MES TÂCHES — deux lignes au plus, la feuille par-dessus ───── */}
              <ColonneProto
                ton="taches"
                titre="Mes tâches"
                testId="carte-taches"
                compteur={taches.ok === true ? resume.total : null}
                enfants={
                  resume.lignes.length === 0 ? (
                    <PhraseVide
                      testId="carte-taches-vide"
                      etat={etatDe(taches)}
                      motif={taches.motif}
                      quandRien={
                        taches.ok && resume.ouvertes > 0
                          ? `Aucune échéance aujourd'hui — ${resume.ouvertes} tâche${resume.ouvertes > 1 ? "s" : ""} ouverte${resume.ouvertes > 1 ? "s" : ""} sans date.`
                          : "Aucune tâche en cours."
                      }
                    />
                  ) : (
                    resume.lignes.map((t) => (
                      <LigneCol
                        key={t.cle}
                        testId={`tache-${t.cle}`}
                        terra={t.ton === "terra"}
                        pc={t.ton === "terra" ? "!" : "☑"}
                        pcTerra={t.ton === "terra"}
                        titre={t.titre}
                        sous={t.sousTitre}
                        onClic={() => setTachesOuvert(true)}
                      />
                    ))
                  )
                }
              />

              {/* ── PROSPECTION — une seule ligne, la feuille par-dessus ──────── */}
              <ColonneProto
                ton="prospection"
                titre="Prospection"
                testId="carte-prospection"
                compteur={null}
                enfants={
                  prospection.ok !== true || !prospection.data.total ? (
                    <PhraseVide
                      testId="carte-prospection-vide"
                      etat={etatDe(prospection)}
                      motif={prospection.motif}
                      quandRien="Aucun prospect collecté."
                    />
                  ) : (
                    (() => {
                      const l = ligneProspection({ ...prospection.data });
                      return (
                        <LigneCol
                          testId="prospection-ligne"
                          pc="◈"
                          titre={l.titre}
                          sous={l.sousTitre}
                          onClic={() => setProspectsOuvert(true)}
                        />
                      );
                    })()
                  )
                }
              />
            </>
          }
        />
      )}

      {/* ⚠️ AUCUNE de ces lectures n'est faite avant le clic. Chaque feuille
          monte le DESSIN livré (proto/FeuillesAccueil.jsx) dans le conteneur
          existant ; sa décision retire sa ligne via `sortir` — sans reload. */}
      {feuille && feuille.origine === "menage" && (
        <CtrlMenageDetail
          ligne={feuille.ligne}
          run={runs.find((r) => r.id === feuille.ligne.runId) || null}
          controle={controles[feuille.ligne.runId]}
          incidents={incidents.data || []}
          creneau={creneauDe(feuille.ligne.runId)}
          nomLogement={nomLogement}
          todayIso={todayIso}
          onFermer={() => setFeuille(null)}
          onAller={aller}
          onControler={(runId) => setControleEmbarque({ runId })}
          onPhoto={(url) => setPhotoOuverte(url)}
        />
      )}
      {feuille && feuille.origine === "sans-agent" && (
        <CtrlSansAgent
          ligne={feuille.ligne}
          creneau={creneauDe(feuille.ligne.runId)}
          optionsAgents={optionsAgents}
          currentUser={currentUser}
          onFermer={() => setFeuille(null)}
          onDecide={() => sortir(feuille.cle)}
          onRafraichir={chargerTout}
        />
      )}
      {feuille && feuille.origine === "depart-reporte" && (
        <CtrlDepartReporte
          ligne={feuille.ligne}
          run={runs.find((r) => r.id === feuille.ligne.runId) || null}
          nomLogement={nomLogement}
          onFermer={() => setFeuille(null)}
          onDecide={() => sortir(feuille.cle)}
        />
      )}
      {feuille && feuille.origine === "reprise" && (
        <CtrlReprise
          ligne={feuille.ligne}
          controle={controles[feuille.ligne.runId]}
          run={runs.find((r) => r.id === feuille.ligne.runId) || null}
          onFermer={() => setFeuille(null)}
          onDecide={() => sortir(feuille.cle)}
        />
      )}
      {feuille && feuille.origine === "ctrl-orphelin" && (
        <CtrlSansPersonne
          ligne={feuille.ligne}
          run={runs.find((r) => r.id === feuille.ligne.runId) || null}
          optionsAgents={optionsAgents}
          onFermer={() => setFeuille(null)}
          onDecide={() => sortir(feuille.cle)}
          onMoiMeme={() => {
            // « Je contrôle maintenant » : l'écran PARTAGÉ du contrôleur
            // s'ouvre sur ce ménage — la feuille de désignation s'efface.
            setControleEmbarque({
              runId: feuille.ligne.runId,
              cle: feuille.cle,
            });
            setFeuille(null);
          }}
        />
      )}
      {controleEmbarque && (
        <CtrlControleEmbarque
          runId={controleEmbarque.runId}
          cfg={cfg}
          currentUser={currentUser}
          onFermer={() => {
            setControleEmbarque(null);
            chargerTout();
          }}
        />
      )}
      {mouvementOuvert && (
        // feuilleResa(nom) du fichier livré : la ligne d'arrivée / de départ
        // ouvre LA réservation — la vraie fiche, pas une reconstitution.
        <ResaV2Modal
          itemId={mouvementOuvert.cible.resaId}
          itemKind={
            mouvementOuvert.kind === "pricing_block"
              ? "pricing_block"
              : "reservation"
          }
          cfg={cfg}
          onClose={() => setMouvementOuvert(null)}
        />
      )}
      {incidentOuvert && (
        <CtrlJugement
          incident={incidentOuvert.incident}
          degForce={incidentOuvert.degForce}
          sousLoc={estSousLoc(incidentOuvert.incident.logement_id)}
          nomLogement={nomLogement}
          proprioNom={proprioNomDe(incidentOuvert.incident.logement_id)}
          adminEmail={currentUser && currentUser.email}
          onFermer={() => setIncidentOuvert(null)}
          onDecide={() =>
            incidentOuvert.degForce
              ? // « Revoir le jugement » peut laisser la ligne AirCover en vie :
                // pas de retrait optimiste ici, le refetch seul fait foi.
                chargerTout()
              : sortir(incidentOuvert.cle)
          }
        />
      )}
      {aircoverOuvert && (
        <CtrlSuivi
          incident={aircoverOuvert.incident}
          deg={aircoverOuvert.deg}
          sousLoc={estSousLoc(aircoverOuvert.incident.logement_id)}
          nomLogement={nomLogement}
          todayIso={todayIso}
          echeance={
            aircoverOuvert.incident._echeanceAircover
              ? {
                  phrase: aircoverOuvert.incident._echeanceAircover,
                  ton: aircoverOuvert.incident._echeanceTon || "neutre",
                }
              : null
          }
          onFermer={() => setAircoverOuvert(null)}
          onDecide={() => sortir(aircoverOuvert.cle)}
          onRevoir={() => {
            setIncidentOuvert({
              incident: aircoverOuvert.incident,
              cle: aircoverOuvert.cle,
              degForce: aircoverOuvert.deg,
            });
            setAircoverOuvert(null);
          }}
        />
      )}
      {horaireOuvert && (
        <CtrlHoraire
          demande={horaireOuvert.demande}
          nomLogement={nomLogement}
          dureeMenageMin={(() => {
            const l = parLogement[horaireOuvert.demande.logement_id];
            const d = l && (l.menage_duree_min ?? l.dureeMenage);
            return Number.isFinite(Number(d)) && Number(d) > 0
              ? Number(d)
              : null;
          })()}
          onFermer={() => setHoraireOuvert(null)}
          onDecide={() => sortir(horaireOuvert.cle)}
        />
      )}
      {/* MICRO RETIRÉ (décision Dimitri 2026-09-07) — il en existait DEUX au
          même endroit (fixed right:20 bottom:20) : celui-ci en z-index 35, et
          `BoutonMicro` (App.jsx, toutes les pages admin) en z-index 70 qui le
          RECOUVRAIT. Depuis le panneau de la voix, leurs messages se
          contredisaient : celui du dessus écoute vraiment, celui-ci disait
          encore « pas encore branché ».
          « Un bouton qui dit pas encore branché alors qu'un autre marche
           juste au-dessus, c'est le pire des deux mondes. » — Dimitri.
          Le flottant global le remplace ; il est présent ici comme partout. */}
      {filOuvert && (
        <CtrlFil
          fil={filOuvert}
          messages={messages.data || []}
          nomLogement={nomLogement}
          propositionPrete={
            filsAvecProposition.has(filOuvert.threadId)
              ? {
                  // D1 — MODE RENVOI : le fil ne décide pas, il emmène. Les boutons
                  // Envoyer/Écarter du bloc opérateur ne s'allument JAMAIS ici.
                  onVoir: () => {
                    setFilOuvert(null);
                    aller("operateur");
                  },
                }
              : null
          }
          onFermer={() => setFilOuvert(null)}
          onEnvoye={() => {
            // [D25] LE FIL OUVERT DEPUIS UN SIGNALEMENT : un envoi réussi EST
            // la suite « répondre » — on la signe alors, pas avant. Si le
            // marquage échoue, la ligne RESTE dans « Ce qui vous attend »
            // (le refetch fait foi) : rien n'est avalé, l'admin la revoit.
            const sig = filOuvert && filOuvert.signalement;
            if (sig) {
              postAction({
                action: "signalement_voyageur_traiter",
                famille: sig.famille,
                id: sig.id,
                issue: "repondu",
              })
                .then(() => sortir(sig.cle))
                .catch(() => chargerTout());
              return;
            }
            chargerTout();
          }}
          // ⚠️ RÉSOLUTION 2026-09-08 : #1301 faisait du micro le « troisième
          // geste » (il EMMÈNE vers l'écran de l'opérateur) parce qu'elle
          // retirait le panneau. main a depuis un panneau vivant
          // (`microOuvert` + MicroAccueil) : on garde LA VERSION DE MAIN,
          // sinon l'état et le composant resteraient là sans jamais s'ouvrir.
          onMicro={() => setMicroOuvert(true)}
        />
      )}
      {signalementOuvert && (
        <CtrlSignalementVoyageur
          famille={signalementOuvert.famille}
          id={signalementOuvert.id}
          titre={signalementOuvert.titre}
          sousTitre={signalementOuvert.sousTitre}
          adminEmail={currentUser && currentUser.email}
          onFermer={() => setSignalementOuvert(null)}
          onDecide={() => sortir(signalementOuvert.cle)}
          onRepondre={(detail) => {
            // [D25] « Répondre » ouvre le fil où la bulle du signalement est
            // déjà arrivée — on RETROUVE ce fil, on n'en fabrique pas un
            // second (règle de lib/voyageur-alerte.js). Rattachement : la
            // réservation (OTA) ou le bloc (Direct) du signalement.
            const m = (messages.data || []).find(
              (x) =>
                x &&
                ((x.reservation_id &&
                  x.reservation_id === detail.reservation_id) ||
                  (x.pricing_block_id &&
                    x.pricing_block_id === detail.reservation_id)),
            );
            if (!m || !(m.thread_id || m.id)) return false;
            setFilOuvert({
              threadId: m.thread_id || m.id,
              titre: signalementOuvert.titre || "Répondre au voyageur",
              signalement: {
                famille: signalementOuvert.famille,
                id: signalementOuvert.id,
                cle: signalementOuvert.cle,
              },
            });
            setSignalementOuvert(null);
            return true;
          }}
          onPhoto={(url) => setPhotoOuverte(url)}
        />
      )}
      {tachesOuvert && (
        <CtrlTaches
          taches={taches.data || []}
          todayIso={todayIso}
          onFermer={() => setTachesOuvert(false)}
          onAller={aller}
        />
      )}
      {prospectsOuvert && (
        <CtrlProspects
          compte={prospection.data || null}
          onFermer={() => setProspectsOuvert(false)}
        />
      )}
      {photoOuverte && (
        // La visionneuse du détail ménage — plein écran navy, un toucher
        // referme (le proto la nomme « visio »).
        <div
          role="dialog"
          aria-modal="true"
          data-testid="accueil-visio"
          onClick={() => setPhotoOuverte(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "#233240",
            zIndex: 9500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <img
            src={photoOuverte}
            alt="Prise pendant le ménage"
            style={{ maxWidth: "94vw", maxHeight: "92vh", borderRadius: 10 }}
          />
        </div>
      )}

      {/* ⚠️ La note du bas dit ce que l'écran NE montre pas — plutôt que de
          laisser croire qu'il montre tout. */}
      <p
        style={{
          fontFamily: DS.fontBody,
          fontSize: 10.5,
          color: GRIS,
          margin: "16px 2px 0",
          lineHeight: 1.5,
        }}
      >
        Le linge n'est pas dans cet écran : la fonction n'existe pas encore, et
        annoncer « aucun article à racheter » laisserait croire à une
        surveillance qui n'a pas lieu.
      </p>
      {/* ⚠️ ET LE MODULE QUI DORT SE DIT AUSSI. Tant que la mise en base des
          signalements n'est pas appliquée, « Ce qui vous attend » n'en montre
          AUCUN — un silence qui ressemblerait à « aucun voyageur n'a rien
          signalé ». La phrase n'apparaît QUE dans ce cas : une fois la
          migration passée, elle disparaît d'elle-même. */}
      {signalements.ok === true &&
        signalements.data &&
        signalements.data.actif === false && (
          <p
            data-testid="accueil-signalements-inertes"
            style={{
              fontFamily: DS.fontBody,
              fontSize: 10.5,
              color: GRIS,
              margin: "8px 2px 0",
              lineHeight: 1.5,
            }}
          >
            {signalements.data.motif}
          </p>
        )}
      {/* ⚠️ L'OPÉRATEUR QUI DORT SE DIT AUSSI (même règle que les
          signalements) : tant que sa mise en base n'est pas appliquée, aucun
          silence qui ressemblerait à « rien à décider ». */}
      {propositions.ok === true &&
        propositions.data &&
        propositions.data.actif === false && (
          <p
            data-testid="accueil-operateur-inerte"
            style={{
              fontFamily: DS.fontBody,
              fontSize: 10.5,
              color: GRIS,
              margin: "8px 2px 0",
              lineHeight: 1.5,
            }}
          >
            {propositions.data.motif}
          </p>
        )}
      {/* ⚠️ LA VEILLE QUI N'A PAS PU ÊTRE LUE SE DIT — sinon une liste amputée
          ressemble à « tout va bien ». Même doctrine que les signalements. */}
      {(veilles.ok === false || (veilles.ok === true && veilles.motif)) && (
        <p
          data-testid="accueil-veille-motif"
          style={{
            fontFamily: DS.fontBody,
            fontSize: 10.5,
            color: GRIS,
            margin: "8px 2px 0",
            lineHeight: 1.5,
          }}
        >
          {veilles.motif}
        </p>
      )}
      {signalements.ok === false && (
        <p
          data-testid="accueil-signalements-echec"
          style={{
            fontFamily: DS.fontBody,
            fontSize: 10.5,
            color: GRIS,
            margin: "8px 2px 0",
            lineHeight: 1.5,
          }}
        >
          {signalements.motif}
        </p>
      )}

      {/* ⚠️ LA RÉSERVE DU MICRO — ordinateur SEULEMENT. Il y flotte en bas à
          droite : sans cette hauteur libre, la dernière ligne resterait sous
          lui, hors de portée, quel que soit le défilement. Sur téléphone il
          vit dans la barre du haut : rien à réserver, et la barre du bas pose
          déjà sa propre marge. */}
      <div
        aria-hidden="true"
        className="fbz-reserve-micro"
        style={{ height: 88 }}
      />
      <style>
        {"@media (max-width: 640px){ .fbz-reserve-micro{ display:none } }"}
      </style>
    </div>
  );
}
